'use client';

import range from 'lodash/range';
import type { UIComponentProps } from '../types/UI';

import { AngleLeftIcon, AngleRightIcon, ChevronDoubleLeft } from '@wolfejs/icons/IconMap';
import { cn } from '../utils/classnames';
import {
  PaginationBase,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from './PaginationBase';

export type PaginationProps = UIComponentProps & {
  /**
   * The current page's index, starting at 0
   */
  currentPageIndex: number;
  /**
   * Total number of pages
   */
  pageCount: number;
  /**
   * Callback to handle logic when a page changes
   */
  onPageChange: (pageIndex: number) => void;
};

export const Pagination = ({
  currentPageIndex,
  pageCount,
  onPageChange,
  className,
}: PaginationProps): React.ReactElement => {
  const lastPage = Math.min(Math.max(currentPageIndex + 3, 4), pageCount);
  const firstPage = Math.max(1, lastPage - 3);

  const goToNextPage = (): void => {
    onPageChange(Math.min(currentPageIndex + 1, pageCount));
  };
  const goToLastPage = (): void => {
    onPageChange(pageCount - 1);
  };
  const goToPreviousPage = (): void => {
    onPageChange(Math.max(currentPageIndex - 1, 0));
  };

  return (
    <PaginationBase className={className}>
      <PaginationContent className="flex h-10 items-center -space-x-px text-base">
        {firstPage > 1 && (
          <PaginationItem>
            <PaginationPrevious
              shape="rect"
              className="border-default flex h-10 items-center justify-center rounded-s-lg border px-4 leading-tight hover:bg-gray-100 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              onClick={() => onPageChange(0)}
            >
              <ChevronDoubleLeft className="w-5" />
            </PaginationPrevious>
          </PaginationItem>
        )}
        <PaginationItem>
          <PaginationPrevious
            shape="rect"
            className={cn(
              'border-default text-default-500 flex h-10 items-center justify-center rounded-s-lg border px-4 leading-tight hover:bg-gray-100 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white',
              { 'rounded-none': firstPage > 1 }
            )}
            onClick={goToPreviousPage}
            disabled={currentPageIndex === 0}
          >
            <AngleLeftIcon className="w-3.5" />
          </PaginationPrevious>
        </PaginationItem>
        {range(firstPage, lastPage).map((page: number) => (
          <PaginationItem key={page}>
            <PaginationLink
              isActive={page - 1 === currentPageIndex}
              className={cn(
                'text-default border-default flex h-10 items-center justify-center rounded-none border px-4 leading-tight hover:bg-gray-100 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white',
                { 'bg-primary-100 text-primary': page - 1 === currentPageIndex }
              )}
              onClick={() => onPageChange(page - 1)}
            >
              {page}
            </PaginationLink>
          </PaginationItem>
        ))}
        {lastPage !== pageCount && (
          <PaginationItem className="text-default border-default flex h-10 items-center justify-center rounded-none border px-4 leading-tight hover:bg-gray-100 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">
            <PaginationEllipsis className="w-3.5" />
          </PaginationItem>
        )}
        <PaginationItem>
          <PaginationLink
            shape="rect"
            isActive={pageCount - 1 === currentPageIndex}
            className={cn(
              'text-default border-default flex h-10 items-center justify-center rounded-none border px-4 leading-tight hover:bg-gray-100 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white',
              { 'bg-primary-100 text-primary': pageCount - 1 === currentPageIndex }
            )}
            onClick={goToLastPage}
            disabled={currentPageIndex + 1 === pageCount}
          >
            {pageCount}
          </PaginationLink>
        </PaginationItem>
        <PaginationItem>
          <PaginationNext
            shape="rect"
            className="border-default text-default-500 flex h-10 items-center justify-center rounded-e-lg border px-4 leading-tight hover:bg-gray-100 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
            onClick={goToNextPage}
            disabled={currentPageIndex + 1 === pageCount}
          >
            <AngleRightIcon className="w-3.5" />
          </PaginationNext>
        </PaginationItem>
      </PaginationContent>
    </PaginationBase>
  );
};
